import { useEffect, useMemo, useRef, useState } from "react";

import styles from "styles/pages/reports/reports.module.scss";

import * as echarts from "echarts";

import classNames from "classnames/bind";
import ComboFilters from "../../ComboFilters";
import { If } from "shared/components/control-statements";
import { Currency } from "shared/utils/Currency";
import { Hairline } from "@components";
import { useDragScroll } from "shared/hooks/useDragScroll";
import { formatNumberWithOneDecimal, Trend } from "pages/Reports/Reports";
import { Numbers } from "shared/utils/Numbers";
import { OlgaEnvironmentsAPI } from "providers/OlgaEnvironmentsAPI";
import { UserStorage } from "shared/tools/UserStorage";
import { useDrag } from "shared/hooks/useDrag";
const css = classNames.bind(styles);

export default function ComparasionOfOperations({
  label_dict,
  data = {},
  isCashback,
  hasUnitsReportFilters,
}) {
  const filters = [
    {
      name: isCashback ? "Valor de concessões" : "Pontos concedidos",
      key: "total_received",
      type: isCashback ? "currency" : "int",
    },
    { name: "Qtd. de concessões", key: "count_received", type: "int" },
    {
      name: isCashback ? "Valor de consumo" : "Pontos consumidos",
      key: "total_used",
      type: isCashback ? "currency" : "int",
    },
    { name: "Qtd. de consumo", key: "count_used", type: "int" },
    ...(hasUnitsReportFilters
      ? []
      : [
          {
            name: isCashback ? "Valor de expirações" : "Pontos expirados",
            key: "total_expired",
            type: isCashback ? "currency" : "int",
          },
          { name: "Qtd. de expirações", key: "count_expired", type: "int" },
          {
            name: isCashback ? "Valor de estorno" : "Pontos estornados",
            key: "total_chargeback",
            type: isCashback ? "currency" : "int",
          },
          { name: "Qtd. de estornos", key: "count_chargeback", type: "int" },
        ]),
    { name: "Taxa de uso", key: "use_ratio", type: "percentage" },
    ...(hasUnitsReportFilters
      ? []
      : [
          {
            name: "Taxa de expiração",
            key: "expired_ratio",
            type: "percentage",
          },
        ]),
    { name: "Tempo médio para uso", key: "average_use_time", type: "days" },
    { name: "ROI do cupom", key: "roi", type: "int" },
  ];

  const getFilterName = (key) => {
    const filter = filters.find((f) => f.key === key);
    return filter ? filter.name : key;
  };

  const getFilterType = (key) => {
    const filter = filters.find((f) => f.key === key);
    return filter ? filter.type : "string";
  };

  const getFormatedValue = (key, value) => {
    if (getFilterType(key) === "currency") {
      return Currency.formatCurrency(value, false, 2);
    } else if (getFilterType(key) === "int") {
      return Numbers.formatInt(value);
    } else if (getFilterType(key) === "float") {
      return Numbers.formatFloat(value, 2);
    } else if (getFilterType(key) === "percentage") {
      return Numbers.percent(+value);
    } else if (getFilterType(key) === "days") {
      return `${formatNumberWithOneDecimal(+value)} dias`;
    }
    return value;
  };

  const [showAll, setShowAll] = useState("see_details");
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState("total_received");

  const memoData = useMemo(() => data, [JSON.stringify(data)]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const chartDOM = document.getElementById("indicators-details-chart");
      if (!chartDOM) return;

      const chart = echarts.init(chartDOM, null, {
        renderer: "svg",
      });

      const getChartData = () => {
        const distribution = (data?.[selectedOption?.id || "units"] || [])?.map(
          (item) => {
            const aliasId = item.unit_id || item.brand_id || item.origin;

            return {
              name:
                label_dict.get(`${selectedOption?.id || "units"}_${aliasId}`) ||
                aliasId ||
                "Origem desconhecida",
              value: item[selectedFilter]?.value || 0,
            };
          }
        );

        return distribution;
      };

      const options = {
        tooltip: {
          trigger: "item",
          responsive: true,
          formatter: (param) => {
            const data = param.data;
            return [
              `<span>${data.name}</span>
                <br/>
                <span style="opacity: 50%">
                  ${getFormatedValue(selectedFilter, data.value)}								
                </span>
              `,
            ].join("");
          },
          backgroundColor: "#242424",
          borderWidth: "0",
          borderColor: "transparent",
          textStyle: {
            color: "#fff",
            fontSize: 10,
          },
        },
        series: [
          {
            type: "pie",
            radius: ["75%"],
            center: ["50%", "45%"],
            label: {
              show: false,
              position: "center",
            },
            data: getChartData(),
          },
        ],
        legend: {
          type: "scroll",
          orient: "horizontal",
          pageIconColor: "#6D6DF2",
          pageIconInactiveColor: "#4C4C4C",
          backgroundColor: "#1C1C1C",
          borderRadius: 10,
          padding: 20,
          pageIconSize: 10,
          pageTextStyle: {
            color: "#B3B3B3",
            fontSize: 10,
            fontWeight: 800,
          },
          pageIcons: {
            horizontal: [
              "path://M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6z",
              "path://M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z",
            ],
          },
          top: "bottom",
          icon: "circle",
          itemWidth: 8,
          itemHeight: 8,
          itemGap: 10,
          textStyle: {
            color: "#808080",
            fontSize: 12,
          },
          inactiveColor: "#555",
          selected: Object.fromEntries(
            getChartData().map((item, index) => [item.name, index < 5])
          ),
        },
      };

      options && chart.setOption(options);

      const ro = new ResizeObserver(() => {
        chart.resize();
      });
      ro.observe(chartDOM);

      return () => {
        ro.unobserve(chartDOM);
        ro.disconnect();
        chart.dispose();
      };
    }
  }, [memoData, selectedOption, selectedFilter, showAll, label_dict]);

  useEffect(() => {
    scrollRef.current.scrollTo(0, 0);
  }, [selectedFilter]);

  const { scrollRef, onMouseDown } = useDragScroll();

  const sortByOrderQuantity = (arr) => {
    return arr.sort((a, b) => {
      return (
        Number(b[selectedFilter]?.value) - Number(a[selectedFilter]?.value)
      );
    });
  };

  data.brands = sortByOrderQuantity(data?.brands || []);
  data.units = sortByOrderQuantity(data?.units || []);
  data.origins = sortByOrderQuantity(data?.origins || []);

  const scrollFiltersRef = useDrag(null);

  return (
    <div className={styles.box2} style={{ minHeight: 632 }}>
      <div className="d-flex align-center justify-between px-20">
        <div className="d-flex align-center gap-5">
          <p className={styles.label}>Comparativo de indicadores</p>
          <span className={styles.info}>
            <i className="fal fa-info-circle" />
          </span>
        </div>
        <div className="d-flex align-center">
          <div className="d-flex align-center gap-10">
            <div
              className={css({
                selectMini: true,
                selected: showAll === "see_details",
              })}
              onClick={() => setShowAll("see_details")}
            >
              Ver detalhes
            </div>
            <div
              className={css({
                selectMini: true,
                selected: showAll === "see_all",
              })}
              onClick={() => setShowAll("see_all")}
            >
              Ver tudo
            </div>
          </div>
        </div>
      </div>

      <If cond={showAll === "see_details"}>
        <div className="mb-15 mt-15">
          <div className={styles.boxFilters} ref={scrollFiltersRef}>
            {filters.map((filter) => (
              <div
                key={filter.key}
                className={css({
                  filter: true,
                  selected: selectedFilter === filter.key,
                })}
                onClick={() => setSelectedFilter(filter.key)}
              >
                {filter.name}
              </div>
            ))}
          </div>
        </div>
      </If>

      <If cond={showAll === "see_all"}>
        <Hairline className="mt-15" />
      </If>

      <div className={styles.container}>
        <If cond={showAll === "see_details"}>
          <div className="px-20 d-flex w-100">
            <div
              className={styles.tableOrdersDistribution}
              onMouseDown={onMouseDown}
              ref={scrollRef}
            >
              <table>
                <thead>
                  <tr>
                    <th>
                      <div className="d-flex">
                        <ComboFilters
                          options={[
                            { name: "Unidades", id: "units" },
                            { name: "Marcas", id: "brands" },
                            ...(UserStorage.isGodMode
                              ? [{ name: "Origens", id: "origins" }]
                              : []),
                          ]}
                          design="two"
                          onSelect={(option) => setSelectedOption(option)}
                          selectedOption={
                            selectedOption || {
                              name: "Unidades",
                              id: "units",
                            }
                          }
                        />
                      </div>
                    </th>
                    <th style={{ width: 200 }}>
                      {getFilterName(selectedFilter || "total_received")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data?.[selectedOption?.id || "units"]?.map((item, index) => {
                    const aliasId =
                      item.unit_id || item.brand_id || item.origin;

                    return (
                      <tr key={index}>
                        <td>
                          <If cond={aliasId}>
                            {label_dict.get(
                              `${selectedOption?.id || "units"}_${aliasId}`
                            ) || aliasId}
                          </If>
                          <If cond={!aliasId}>Origem desconhecida</If>
                        </td>
                        <td>
                          <div className="d-flex align-center justify-between">
                            <p className={styles.value}>
                              {getFormatedValue(
                                selectedFilter,
                                item[selectedFilter]?.value
                              )}
                            </p>
                            {/* <Trend growth={item[selectedFilter]?.growth} /> */}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className={styles.chartContainer}>
              <p className={styles.title}>
                Proporção de {getFilterName(selectedFilter || "total_received")}
              </p>
              <div
                id="indicators-details-chart"
                style={{
                  width: "100%",
                  height: "100%",
                  minHeight: 350,
                  margin: "auto",
                }}
              />
            </div>
          </div>
        </If>

        <If cond={showAll === "see_all"}>
          <div
            className={styles.tableOrdersDistributionFull}
            onMouseDown={onMouseDown}
            ref={scrollRef}
          >
            <table>
              <thead>
                <tr>
                  <th
                    style={{
                      position: "sticky",
                      left: 0,
                      zIndex: 200,
                      backgroundColor: "#1f1f1f",
                    }}
                  >
                    <div className="d-flex">
                      <ComboFilters
                        options={[
                          { name: "Unidades", id: "units" },
                          { name: "Marcas", id: "brands" },
                          ...(UserStorage.isGodMode
                            ? [{ name: "Origens", id: "origins" }]
                            : []),
                        ]}
                        design="two"
                        onSelect={(option) => setSelectedOption(option)}
                        selectedOption={
                          selectedOption || {
                            name: "Unidades",
                            id: "units",
                          }
                        }
                      />
                    </div>
                  </th>
                  <th style={{ paddingLeft: 35 }}>
                    {isCashback ? "Valor de concessões" : "Pontos concedidos"}
                  </th>
                  <th style={{ paddingLeft: 35 }}>Qtd. de concessões</th>
                  <th style={{ paddingLeft: 35 }}>
                    {isCashback ? "Valor de consumo" : "Pontos consumidos"}
                  </th>
                  <th style={{ paddingLeft: 35 }}>Qtd. de consumo</th>
                  <If cond={!hasUnitsReportFilters}>
                    <th style={{ paddingLeft: 35 }}>
                      {isCashback ? "Valor de expirações" : "Pontos expirados"}
                    </th>
                    <th style={{ paddingLeft: 35 }}>Qtd. de expirações</th>
                    <th style={{ paddingLeft: 35 }}>
                      {isCashback ? "Valor de estorno" : "Pontos estornados"}
                    </th>
                    <th style={{ paddingLeft: 35 }}>Qtd. de estornos</th>
                  </If>
                  <th style={{ paddingLeft: 35 }}>Taxa de uso</th>
                  <If cond={!hasUnitsReportFilters}>
                    <th style={{ paddingLeft: 35 }}>Taxa de expiração</th>
                  </If>
                  <th style={{ paddingLeft: 35 }}>Tempo médio para uso</th>
                  <th style={{ paddingLeft: 35 }}>ROI do cupom</th>
                </tr>
              </thead>
              <tbody>
                {data?.[selectedOption?.id || "units"]?.map((item, index) => {
                  const aliasId = item.unit_id || item.brand_id || item.origin;

                  return (
                    <tr key={index}>
                      <td
                        style={{
                          position: "sticky",
                          left: 0,
                          zIndex: 3,
                          backgroundColor: "#1c1c1c",
                        }}
                      >
                        <If cond={aliasId}>
                          {label_dict.get(
                            `${selectedOption?.id || "units"}_${aliasId}`
                          ) || aliasId}
                        </If>
                        <If cond={!aliasId}>Origem desconhecida</If>
                      </td>
                      <td>
                        <div
                          className="d-flex align-center"
                          style={{ paddingLeft: 20 }}
                        >
                          <p className={styles.value}>
                            {isCashback
                              ? Currency.formatCurrency(
                                  item.total_received.value
                                )
                              : Numbers.formatInt(item.total_received.value)}
                          </p>
                        </div>
                      </td>
                      <td>
                        <div
                          className="d-flex align-center"
                          style={{ paddingLeft: 20 }}
                        >
                          <p className={styles.value}>
                            {Numbers.formatInt(item.count_received.value)}
                          </p>
                        </div>
                      </td>
                      <td>
                        <div
                          className="d-flex align-center"
                          style={{ paddingLeft: 20 }}
                        >
                          <p className={styles.value}>
                            {isCashback
                              ? Currency.formatCurrency(item.total_used.value)
                              : Numbers.formatInt(item.total_used.value)}
                          </p>
                        </div>
                      </td>
                      <td>
                        <div
                          className="d-flex align-center"
                          style={{ paddingLeft: 20 }}
                        >
                          <p className={styles.value}>
                            {Numbers.formatInt(item.count_used.value)}
                          </p>
                        </div>
                      </td>
                      <If cond={!hasUnitsReportFilters}>
                        <td>
                          <div
                            className="d-flex align-center"
                            style={{ paddingLeft: 20 }}
                          >
                            <p className={styles.value}>
                              {isCashback
                                ? Currency.formatCurrency(
                                    item.total_expired.value
                                  )
                                : Numbers.formatInt(item.total_expired.value)}
                            </p>
                          </div>
                        </td>
                        <td>
                          <div
                            className="d-flex align-center"
                            style={{ paddingLeft: 20 }}
                          >
                            <p className={styles.value}>
                              {Numbers.formatInt(item.count_expired.value)}
                            </p>
                          </div>
                        </td>
                        <td>
                          <div
                            className="d-flex align-center"
                            style={{ paddingLeft: 20 }}
                          >
                            <p className={styles.value}>
                              {isCashback
                                ? Currency.formatCurrency(
                                    item.total_chargeback.value
                                  )
                                : Numbers.formatInt(
                                    item.total_chargeback.value
                                  )}
                            </p>
                          </div>
                        </td>
                        <td>
                          <div
                            className="d-flex align-center"
                            style={{ paddingLeft: 20 }}
                          >
                            <p className={styles.value}>
                              {Numbers.formatInt(item.count_chargeback.value)}
                            </p>
                          </div>
                        </td>
                      </If>

                      <td>
                        <div
                          className="d-flex align-center"
                          style={{ paddingLeft: 20 }}
                        >
                          <p className={styles.value}>
                            {Numbers.formatPercent(item.use_ratio?.value)}
                          </p>
                        </div>
                      </td>
                      <If cond={!hasUnitsReportFilters}>
                        <td>
                          <div
                            className="d-flex align-center"
                            style={{ paddingLeft: 20 }}
                          >
                            <p className={styles.value}>
                              {Numbers.formatPercent(item.expired_ratio?.value)}
                            </p>
                          </div>
                        </td>
                      </If>
                      <td>
                        <div
                          className="d-flex align-center"
                          style={{ paddingLeft: 20 }}
                        >
                          <p className={styles.value}>
                            {Numbers.formatInt(item.average_use_time?.value)}{" "}
                            dias
                          </p>
                        </div>
                      </td>
                      <td>
                        <div
                          className="d-flex align-center"
                          style={{ paddingLeft: 20 }}
                        >
                          <p className={styles.value}>
                            {Numbers.formatInt(item.roi?.value)}
                          </p>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {Array.from({
                  length: Math.max(
                    0,
                    12 - data?.[selectedOption?.id || "units"]?.length
                  ),
                }).map((_, i) => (
                  <tr key={`empty-${i}`}>
                    <td
                      style={{
                        position: "sticky",
                        left: 0,
                        zIndex: 3,
                        backgroundColor: "#1c1c1c",
                      }}
                    >
                      &nbsp;
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </If>
      </div>
    </div>
  );
}
